import { useState } from "react";
import { Users, Phone, MapPin, Calendar, Megaphone, Heart } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EXTRA_PHOTO = "https://customer-assets.emergentagent.com/job_144b4408-104d-4a3e-8d1d-a71d38c76165/artifacts/gadcjwgo_Extra%20Picture%20if%20Needed.jpg";

const VOLUNTEER_ACTIVITIES = [
  { id: "phone_banking", label: "Phone Banking", icon: Phone },
  { id: "canvassing", label: "Door-to-Door Canvassing", icon: MapPin },
  { id: "events", label: "Event Support", icon: Calendar },
  { id: "social_media", label: "Social Media", icon: Megaphone },
  { id: "community", label: "Community Outreach", icon: Users },
  { id: "other", label: "Other / Flexible", icon: Heart },
];

const VolunteerPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    zip_code: "",
    interests: [],
    availability: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInterestToggle = (id) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(id)
        ? prev.interests.filter((i) => i !== id)
        : [...prev.interests, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/volunteer`, formData);
      toast.success("Thank you for signing up! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        zip_code: "",
        interests: [],
        availability: "",
      });
    } catch (error) {
      // Error handled via UI state
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="volunteer-page" className="bg-[#0B1A3B] pt-16 md:pt-20">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A3B] via-[#0F2251] to-[#0B1A3B]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#D4AF37]/[0.03] rounded-full blur-[100px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Get Involved</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white mb-4">Join the Team</h1>
          <p className="text-lg text-white/50 max-w-2xl mx-auto">
            Real change starts with people like you. Sign up to volunteer and help us build a Nevada where every family can succeed.
          </p>
        </div>
      </section>

      {/* Volunteer Form */}
      <section className="py-16 md:py-24 relative">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image & Info */}
            <div>
              <div className="relative mb-8">
                <div className="absolute -inset-1 bg-gradient-to-br from-[#D4AF37]/20 to-transparent blur-xl" />
                <div className="relative bg-white/[0.03] border border-white/10 p-1">
                  <img src={EXTRA_PHOTO} alt="Join our team" data-testid="volunteer-image" className="w-full object-cover" />
                </div>
              </div>
              <div className="bg-white/[0.03] border border-white/10 p-8">
                <h2 className="text-lg font-bold text-white mb-4">Why Volunteer?</h2>
                <ul className="space-y-4">
                  {["Make a real difference in your community", "Connect with like-minded Nevadans", "Build skills and leadership experience", "Be part of something bigger than yourself"].map((item, i) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center bg-[#D4AF37] text-black text-xs font-bold">{i + 1}</span>
                      <span className="text-white/50">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Form */}
            <div>
              <form onSubmit={handleSubmit} className="bg-white/[0.03] border border-white/10 p-8">
                <h2 className="text-lg font-bold text-white mb-6">Sign Up</h2>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-white/40">Full Name *</Label>
                    <Input id="name" name="name" required value={formData.name} onChange={handleInputChange} className="mt-2 rounded-none bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]" data-testid="volunteer-name-input" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-white/40">Email *</Label>
                      <Input id="email" name="email" type="email" required value={formData.email} onChange={handleInputChange} className="mt-2 rounded-none bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]" data-testid="volunteer-email-input" />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-white/40">Phone</Label>
                      <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} className="mt-2 rounded-none bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]" data-testid="volunteer-phone-input" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="zip_code" className="text-xs font-bold uppercase tracking-wider text-white/40">ZIP Code *</Label>
                    <Input id="zip_code" name="zip_code" required value={formData.zip_code} onChange={handleInputChange} className="mt-2 rounded-none bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]" data-testid="volunteer-zip-input" />
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-white/40 mb-4">How would you like to help?</p>
                    <div className="grid grid-cols-2 gap-3">
                      {VOLUNTEER_ACTIVITIES.map((activity) => (
                        <label
                          key={activity.id}
                          data-testid={`volunteer-interest-${activity.id}`}
                          className={`flex items-center gap-3 p-4 cursor-pointer border transition-all ${
                            formData.interests.includes(activity.id)
                              ? "border-[#D4AF37] bg-[#D4AF37]/10"
                              : "border-white/10 hover:border-white/20"
                          }`}
                        >
                          <Checkbox
                            checked={formData.interests.includes(activity.id)}
                            onCheckedChange={() => handleInterestToggle(activity.id)}
                            className="rounded-none border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                          />
                          <activity.icon className="w-4 h-4 text-white/40" />
                          <span className="text-sm text-white/70">{activity.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="availability" className="text-xs font-bold uppercase tracking-wider text-white/40">Availability (optional)</Label>
                    <Input id="availability" name="availability" value={formData.availability} onChange={handleInputChange} className="mt-2 rounded-none bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]" placeholder="e.g., Weekends, Evenings, Flexible" data-testid="volunteer-availability-input" />
                  </div>

                  <button type="submit" disabled={isSubmitting} data-testid="volunteer-submit-btn" className="w-full py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(212,175,55,0.3)] disabled:opacity-50">
                    {isSubmitting ? "Signing Up..." : "Join the Team"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VolunteerPage;
